/**
 * CommunityBattleFeed Component
 *
 * Displays recent battles from all players in the community.
 * Supports real-time updates, search, and expansion.
 */

import { useState } from 'react';
import { useCommunityBattleFeed, CommunityBattle } from '../../hooks/useCommunityBattleFeed';
import styles from './CommunityBattleFeed.module.css';

interface CommunityBattleFeedProps {
  initialLimit?: number;
  maxExpanded?: number;
  showSearch?: boolean;
}

/**
 * Format date to relative time string
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Individual battle entry component
 */
function BattleEntry({ battle, isNew }: { battle: CommunityBattle; isNew?: boolean }) {
  const isWin = battle.winner !== 'draw';
  const winnerName = isWin
    ? (battle.winner === 'player1' ? battle.player1.displayName : battle.player2.displayName)
    : null;
  const loserName = isWin
    ? (battle.winner === 'player1' ? battle.player2.displayName : battle.player1.displayName)
    : null;

  return (
    <div className={`${styles.battleEntry} ${isNew ? styles.battleEntryNew : ''}`}>
      <div className={styles.battleResult}>
        {isWin ? (
          <>
            <span className={styles.winnerName}>{winnerName}</span>
            <span className={styles.vsText}> defeated </span>
            <span className={styles.loserName}>{loserName}</span>
          </>
        ) : (
          <>
            <span className={styles.drawName}>{battle.player1.displayName}</span>
            <span className={styles.vsText}> drew with </span>
            <span className={styles.drawName}>{battle.player2.displayName}</span>
          </>
        )}
      </div>
      <div className={styles.battleMeta}>
        <span className={styles.battleName}>{battle.battleName}</span>
        <span className={styles.separator}>-</span>
        <span className={styles.timestamp}>{formatRelativeTime(battle.completedAt)}</span>
      </div>
    </div>
  );
}

/**
 * CommunityBattleFeed Component
 *
 * Shows recent community battles with real-time updates, search, and pagination.
 *
 * @param initialLimit - Number of battles to show in default view (default: 5)
 * @param maxExpanded - Maximum battles to show in expanded view (default: 20)
 * @param showSearch - Whether to show search input when expanded (default: true)
 *
 * @example
 * ```tsx
 * <CommunityBattleFeed />
 * <CommunityBattleFeed initialLimit={10} maxExpanded={50} />
 * <CommunityBattleFeed showSearch={false} />
 * ```
 */
export function CommunityBattleFeed({
  initialLimit = 5,
  maxExpanded = 20,
  showSearch = true
}: CommunityBattleFeedProps) {
  const {
    battles,
    total,
    hasMore,
    isLoading,
    error,
    isExpanded,
    searchQuery,
    setSearchQuery,
    toggleExpanded,
    loadMore,
    newBattleIds
  } = useCommunityBattleFeed(initialLimit, maxExpanded);

  const [searchInput, setSearchInput] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Community Battles</h3>
        {total > 0 && (
          <span className={styles.count}>{total} total</span>
        )}
      </div>

      {showSearch && isExpanded && (
        <form className={styles.searchForm} onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search by player name..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.searchButton}>
            Search
          </button>
          {searchQuery && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClearSearch}
            >
              Clear
            </button>
          )}
        </form>
      )}

      {isLoading && battles.length === 0 && (
        <div className={styles.loading}>
          <span className={styles.loadingSpinner}></span>
          Loading battles...
        </div>
      )}

      {error && (
        <div className={styles.error}>{error}</div>
      )}

      {!isLoading && !error && battles.length === 0 && (
        <div className={styles.empty}>
          {searchQuery ? 'No battles found matching your search' : 'No battles yet'}
        </div>
      )}

      {battles.length > 0 && (
        <div className={styles.battleList}>
          {battles.map((battle) => (
            <BattleEntry
              key={battle.battleId}
              battle={battle}
              isNew={newBattleIds?.has(battle.battleId)}
            />
          ))}
        </div>
      )}

      <div className={styles.footer}>
        {!isExpanded && battles.length >= initialLimit && (
          <button onClick={toggleExpanded} className={styles.expandButton}>
            Show More
          </button>
        )}
        {isExpanded && (
          <>
            {hasMore && (
              <button
                onClick={loadMore}
                className={styles.loadMoreButton}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Load More'}
              </button>
            )}
            <button onClick={toggleExpanded} className={styles.collapseButton}>
              Show Less
            </button>
          </>
        )}
      </div>
    </div>
  );
}
